import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import PdfReader from "../components/PdfReader";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, ArrowLeft, FileText, Clock } from "lucide-react";

export default function Learn() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login", { replace: true }); return; }
    if (user) {
      api.get(`/courses/${slug}?lang=${lang}`).then((r) => {
        if (!r.data.owned || r.data.locked) { navigate(`/course/${slug}`, { replace: true }); return; }
        setCourse(r.data);
      }).catch(() => navigate("/dashboard"));
    }
  }, [user, authLoading, slug, lang, navigate]);

  const toggle = async (idx) => {
    if (saving) return;
    const set = new Set(course.completed_modules);
    if (set.has(idx)) set.delete(idx); else set.add(idx);
    const modules = Array.from(set);
    setSaving(true);
    try {
      const res = await api.post(`/courses/${slug}/progress?lang=${lang}`, { completed_modules: modules });
      setCourse(res.data);
      if (res.data.completed) toast.success(t("learn.completedToast"));
    } catch (e) {
      toast.error(t("learn.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (!course) {
    return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="animate-spin text-[#F5842B]" /></div>;
  }

  const accent = course.accent;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <Navbar />
      <section className="pt-24 pb-6 max-w-4xl mx-auto px-5 sm:px-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="learn-back-btn">
          <ArrowLeft size={16} /> {t("learn.back")}
        </Link>
        <div className="flex items-center gap-3 mt-5">
          <span className="font-mono text-xs tracking-[0.25em] px-3 py-1.5 rounded-full font-semibold" style={{ backgroundColor: `${accent}22`, color: accent, border: `1px solid ${accent}55` }}>{course.code}</span>
        </div>
        <h1 className="font-display text-4xl font-extrabold mt-4">{course.title}</h1>
        <p className="mt-2" style={{ color: accent }}>{course.subtitle}</p>

        <div className="mt-6 glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-300 font-medium">{t("learn.progress")}</span>
            <span className="font-mono text-sm" style={{ color: accent }}>{course.progress_percent}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${course.progress_percent}%`, backgroundColor: accent }} />
          </div>
          {course.completed && (
            <div className="mt-4 flex items-center gap-2 text-emerald-300 text-sm font-semibold" data-testid="learn-completed">
              <CheckCircle2 size={17} /> {t("learn.cert")}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        {course.has_pdf ? (
          <div className="mb-10" data-testid="course-pdf-viewer">
            <h2 className="font-display text-2xl font-bold mb-5 flex items-center gap-2">
              <FileText size={22} style={{ color: accent }} /> {t("learn.consult")}
            </h2>
            <PdfReader slug={course.slug} lang={lang} accent={accent} />
          </div>
        ) : (
          <div className="mb-10 glass rounded-2xl p-6 flex items-start gap-4 border border-amber-400/30" data-testid="course-pdf-comingsoon">
            <Clock size={22} className="text-amber-300 shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg font-bold text-white">{t("learn.comingSoonTitle")}</div>
              <p className="text-sm text-slate-400 mt-1">{t("learn.comingSoonMsg")}</p>
            </div>
          </div>
        )}
        <h2 className="font-display text-2xl font-bold mb-5">{t("learn.modules")}</h2>
        <div className="space-y-3">
          {course.topics.map((tp, i) => {
            const done = course.completed_modules.includes(i);
            return (
              <button key={i} onClick={() => toggle(i)} data-testid={`learn-module-${i}`}
                className="w-full text-left glass rounded-xl p-5 flex items-center gap-4 hover:border-white/20 transition-colors"
                style={done ? { borderColor: `${accent}66` } : {}}>
                {done ? <CheckCircle2 size={22} style={{ color: accent }} className="shrink-0" /> : <Circle size={22} className="text-slate-500 shrink-0" />}
                <div className="flex-1">
                  <span className="font-mono text-xs text-slate-500">{t("learn.module")} {String(i + 1).padStart(2, "0")}</span>
                  <div className={`font-medium ${done ? "text-white" : "text-slate-200"}`}>{tp}</div>
                </div>
                <span className="font-mono text-xs" style={{ color: done ? accent : "#64748b" }}>{done ? t("learn.done") : t("learn.todo")}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
